"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Variables = void 0;
const mobx_1 = require("mobx");
const moment_1 = __importDefault(require("moment"));
const lodash_1 = __importDefault(require("lodash"));
const common_utils_1 = require("@riseupil/common-utils");
const utils_1 = require("../utils");
const consts_1 = require("../consts");
class Variables {
    constructor(reactiveCashflow, dateLocale = common_utils_1.DateLocales.EN_IL) {
        var _a;
        this.budgetDate = reactiveCashflow.budgetDate;
        this.lastUpdatedAt = reactiveCashflow.lastUpdatedAt;
        this.totalVariables = (_a = reactiveCashflow.variableBalancedAmount) !== null && _a !== void 0 ? _a : 0;
        this.transactions = reactiveCashflow.variableTransactions || [];
        this.cashflowStartDay = reactiveCashflow.cashflowStartDay;
        this.dateLocale = dateLocale;
    }
    get startOfFirstWeek() {
        return common_utils_1.localizedMoment(this.dateLocale, this.budgetDate).startOf('month').date(this.cashflowStartDay).startOf('week');
    }
    get plannedTransactions() {
        return this.transactions.filter(t => t.planned);
    }
    get plannedSum() {
        return utils_1.sumTransactions(this.plannedTransactions);
    }
    get variableTransactions() {
        return this.transactions.filter(t => !t.planned);
    }
    get transactionsByWeek() {
        return lodash_1.default.groupBy(this.variableTransactions, ({ transactionWeekInMonth }) => transactionWeekInMonth);
    }
    get balancedWeeksTransactionSum() {
        return lodash_1.default.chain(this.weeksMeta)
            .filter(week => week.timeframe !== consts_1.WEEK_TIMEFRAME.CURRENT)
            .flatMap(week => this.transactionsByWeek[week.index])
            .compact()
            .thru(transactions => utils_1.sumTransactions(transactions))
            .value() || 0;
    }
    get dailyVariables() {
        const variablesRemaining = (this.totalVariables - this.balancedWeeksTransactionSum - this.plannedSum);
        // avoid zero division if no days remaining
        return this.remainingDays > 0 ? (variablesRemaining / this.remainingDays) : 0;
    }
    get remainingDays() {
        return lodash_1.default.chain(this.weeksMeta)
            .reject(week => week.timeframe === consts_1.WEEK_TIMEFRAME.PAST)
            .sumBy(week => week.days)
            .value();
    }
    get weeksMeta() {
        return common_utils_1.getWeeks(this.budgetDate, this.cashflowStartDay || 1, this.dateLocale || common_utils_1.DateLocales.EN_IL).map(week => {
            return {
                ...week,
                timeframe: common_utils_1.getWeekTimeframe({ referenceDate: this.lastUpdatedAt, week }),
            };
        });
    }
    get potentialWeeks() {
        return lodash_1.default.map(this.weeksMeta, weekMeta => {
            const sum = utils_1.sumTransactions(this.transactionsByWeek[weekMeta.index]);
            return lodash_1.default.extend({
                available: weekMeta.timeframe === consts_1.WEEK_TIMEFRAME.PAST ? sum : (this.dailyVariables * weekMeta.days),
                sum,
                transactions: this.transactionsByWeek[weekMeta.index],
            }, weekMeta);
        });
    }
    get weeks() {
        const futureWeekDays = lodash_1.default.chain(this.weeksMeta).filter(w => w.timeframe === consts_1.WEEK_TIMEFRAME.FUTURE).sumBy(w => w.days).value();
        return lodash_1.default.map(this.potentialWeeks, week => {
            let remaining;
            if (week.timeframe === consts_1.WEEK_TIMEFRAME.PAST) {
                remaining = 0;
            }
            else if (week.timeframe === consts_1.WEEK_TIMEFRAME.CURRENT) {
                remaining = lodash_1.default.max([week.available - week.sum, 0]);
            }
            else {
                const deficitToBalance = (_getCurrentMonthDeficit(this.potentialWeeks) / futureWeekDays) * week.days;
                remaining = lodash_1.default.max([(week.available - week.sum) + deficitToBalance, 0]);
            }
            return lodash_1.default.extend({ remaining }, week);
        });
    }
    toJson() {
        const weeks = lodash_1.default.map(this.weeks, week => {
            return {
                weekIndex: week.index,
                days: week.days,
                startDate: moment_1.default(week.startDate).format(consts_1.DAY_FORMAT),
                endDate: moment_1.default(week.endDate).format(consts_1.DAY_FORMAT),
                sum: week.sum,
                remaining: week.remaining,
                available: week.available,
                transactions: lodash_1.default.cloneDeep(week.transactions) || [],
            };
        });
        return {
            budgetDate: this.budgetDate,
            lastUpdatedAt: this.lastUpdatedAt,
            totalVariables: this.totalVariables,
            available: this.totalVariables,
            sum: utils_1.sumTransactions(this.transactions),
            plannedTransactions: lodash_1.default.cloneDeep(this.plannedTransactions),
            weeks,
        };
    }
    getCashculations() {
        const weeks = lodash_1.default.map(this.weeks, week => {
            return {
                weekIndex: week.index,
                days: week.days,
                startDate: moment_1.default(week.startDate).format(consts_1.DAY_FORMAT),
                endDate: moment_1.default(week.endDate).format(consts_1.DAY_FORMAT),
                sum: week.sum,
                remaining: week.remaining,
                available: week.available,
            };
        });
        return {
            budgetDate: this.budgetDate,
            lastUpdatedAt: this.lastUpdatedAt,
            totalVariables: this.totalVariables,
            available: this.totalVariables,
            sum: utils_1.sumTransactions(this.transactions),
            weeks,
        };
    }
}
__decorate([
    mobx_1.observable
], Variables.prototype, "budgetDate", void 0);
__decorate([
    mobx_1.observable
], Variables.prototype, "lastUpdatedAt", void 0);
__decorate([
    mobx_1.observable
], Variables.prototype, "totalVariables", void 0);
__decorate([
    mobx_1.observable
], Variables.prototype, "transactions", void 0);
__decorate([
    mobx_1.computed
], Variables.prototype, "startOfFirstWeek", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "plannedTransactions", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "plannedSum", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "variableTransactions", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "transactionsByWeek", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "balancedWeeksTransactionSum", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "dailyVariables", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "remainingDays", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "weeksMeta", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "potentialWeeks", null);
__decorate([
    mobx_1.computed
], Variables.prototype, "weeks", null);
exports.Variables = Variables;
function _getCurrentMonthDeficit(weeks) {
    const currentWeek = lodash_1.default.find(weeks, w => w.timeframe === consts_1.WEEK_TIMEFRAME.CURRENT);
    if (!currentWeek) {
        return 0;
    }
    return Math.min(currentWeek.available - currentWeek.sum, 0);
}
//# sourceMappingURL=variables.js.map